import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { toast } from '../../toasts';
import axios from 'axios';
import moment from 'moment';
import debtService from '../../services/debt.service';
import { getError } from '../../utils';
import PropTypes from 'prop-types';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { Button } from '../EntityList';

const normalizePhone = (raw) => raw?.replace(/\D/g, '') || '';
const isValidPhone = (raw) => {
	const digits = normalizePhone(raw);
	return digits.length === 11 && digits.startsWith('598');
};
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function DebtDueReminderModal({ show, onHide, due }) {
	const [channel, setChannel] = useState('whatsapp');
	const [contactInput, setContactInput] = useState('');
	const [sending, setSending] = useState(false);
	const [companyName, setCompanyName] = useState('');
	console.log('companyName', companyName);
	useEffect(() => {
		if (!show) return;
		axios.get('/api/config/public')
			.then(({ data }) => setCompanyName(data.companyName || ''))
			.catch(() => {});
	}, [show]);

	useEffect(() => {
		if (!due) return;
		if (channel === 'whatsapp') {
			setContactInput(due.debt?.owner?.phone || '');
		} else {
			setContactInput(due.debt?.owner?.email || '');
		}
	}, [channel, due]);

	const buildBody = () => {
		const ownerName = due?.debt?.owner?.name || '';
		const debtCustomId = due?.debt?.customId || '';
		const expiration = due?.expiration
			? moment.utc(due.expiration).format('DD-MM-YYYY')
			: '';
		return `Hola ${ownerName}, recordamos el pago de ${debtCustomId} que vence ${expiration}`;
	};

	const isContactValid = () => {
		if (!contactInput) return false;
		return channel === 'whatsapp' ? isValidPhone(contactInput) : isValidEmail(contactInput);
	};

	const handleSubmit = async () => {
		if (!isContactValid()) return;

		if (channel === 'whatsapp') {
			const digits = normalizePhone(contactInput);
			const text = encodeURIComponent(buildBody());
			window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer');
			onHide();
			return;
		}

		// email channel
		try {
			setSending(true);
			await debtService.sendDueReminder(due._id, { email: contactInput });
			toast.success('Recordatorio enviado por email');
			onHide();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSending(false);
		}
	};

	const handleClose = () => {
		if (sending) return;
		setChannel('whatsapp');
		onHide();
	};

	return (
		<Modal show={show} onHide={handleClose} animation={false} centered>
			<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton={!sending}>
				<Modal.Title className='tw:text-[15px] tw:font-semibold'>Enviar recordatorio</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:relative tw:bg-surface tw:px-5 tw:py-5'>
				{sending && <SpinnerOverlay />}
				<Form.Group>
					<Form.Label>Canal</Form.Label>
					<Form.Select
						value={channel}
						onChange={(e) => setChannel(e.target.value)}
						disabled={sending}
					>
						<option value="whatsapp">WhatsApp</option>
						<option value="email">Email</option>
					</Form.Select>
				</Form.Group>

				<Form.Group className="mt-3">
					<Form.Label>
						{channel === 'whatsapp' ? 'Número de WhatsApp' : 'Email'}
					</Form.Label>
					<Form.Control
						type={channel === 'whatsapp' ? 'tel' : 'email'}
						placeholder={channel === 'whatsapp' ? '59891234567' : 'ejemplo@correo.com'}
						value={contactInput}
						onChange={(e) => setContactInput(e.target.value)}
						onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
						isInvalid={!!contactInput && !isContactValid()}
						disabled={sending}
					/>
					{channel === 'whatsapp' && (
						<Form.Text className="text-muted">
							Incluir código de país. Ej: 59891234567 (Uruguay: 598 + 8 dígitos)
						</Form.Text>
					)}
					<Form.Control.Feedback type="invalid">
						{channel === 'whatsapp'
							? 'Número inválido. Ej: 59891234567'
							: 'Email inválido'}
					</Form.Control.Feedback>
				</Form.Group>
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
				<Button onClick={handleClose} disabled={sending}>Cancelar</Button>
				<Button
					variant='primary'
					onClick={handleSubmit}
					disabled={sending || !isContactValid()}
				>
					Enviar
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

DebtDueReminderModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onHide: PropTypes.func.isRequired,
	due: PropTypes.object,
};
